import FormSerialize from "form-serialize";
import Component from "../../../lib/component";
import DOMUpdateEvent from "../../../helpers/dom_update_event";
import {XHRFetcher} from "../../../lib/xhr_fetcher";

import {InquiryInterestsField} from "./components/inquiry_interests_field";
import {LocationField} from "./components/location_field";
import {DatesField} from "./components/dates_field";
import {QuantityPeopleField} from "./components/quantity_people_field";
import {MessageField} from "./components/message_field";
import {PersonNameField} from "./components/person_name_field";
import {PersonEmailField} from "./components/person_email_field";
import {AgreementField} from "./components/agreement_field";

import {ButtonsFieldset} from "./components/buttons_fieldset";
import {SubmitButton} from "./components/submit_button";
import {isEmpty} from "../../../helpers/object";
import {ModalboxConfig} from "../../../components/modalbox/config";
import {getGlobalComponentOfType} from "../../../lib/global_components";

export const InquiryFormConfig = {
    componentTypeKey: 'InquiryForm',
    baseHtmlClass: 'inquiry_form',
    selector: '.inquiry_form',

    datasetConfigKey: 'inquiry_form__config',

    initializedHtmlClass: 'inquiry_form--inited',

    commsBaseHtmlClass: 'inquiry_form__comms',
    commsLegendBaseHtmlClass: 'inquiry_form__comms_legend',
    errorsBaseHtmlClass: 'inquiry_form__errors',
    warningsBaseHtmlClass: 'inquiry_form__warnings',

    formSelector: 'form.inquiry_form__form',

    changeEventName: 'InquiryFormChanged',
    submitEventName: 'InquiryFormSubmit',
};

export class InquiryForm extends Component {

    static createCommsElement() {
        let element = InquiryForm.createElement(`${InquiryFormConfig.commsBaseHtmlClass}`);
        let legendElement = InquiryForm.createElement(`${InquiryFormConfig.commsLegendBaseHtmlClass}`, {tagName: 'legend'});
        legendElement.innerHTML = '<span>Notice</span>';
        element.insertBefore(legendElement, null);
        return element;
    }

    static createErrorsElement() {
        return InquiryForm.createElement(`${InquiryFormConfig.errorsBaseHtmlClass}`);
    }

    static createWarningsElement() {
        return InquiryForm.createElement(`${InquiryFormConfig.warningsBaseHtmlClass}`);
    }

    constructor(id_or_element, config) {
        super({
            element: id_or_element,
            config: InquiryFormConfig
        });

        this.setDatasetConfig(InquiryFormConfig.datasetConfigKey);

        // We store whether there is loading happening.
        // Whenever loading of something starts we add to the `loading` object.
        // And when that loading is complete we remove it from the object.
        this.loading = {};

        this.document = document;
        this.window = window;
        this.modalbox = this.getModalbox();

        this.submitWithXHR = this.getDatasetConfigValue('submit_with_xhr') ?? true;

        this.contentForSubmitting = this.getContentForSubmitting();

        this.formElement = this.element.querySelector(InquiryFormConfig.formSelector);

        this.url = this.formElement.getAttribute('action');
        this.method = this.formElement.getAttribute('method');
        this.form_type = this.formElement.getAttribute('form_type');

        // We store whether the form has been interacted with (fields changed) by the user.
        this.interacted = false;

        // We store whether the form has been committed by the user.
        // Committed means the user has attempted to submit the form (but the form values have not been POSTed to the server).
        // Once the form has been committed (without errors) then we can POST to the server.
        // Errors are not rendered until the first commit. Then they are rendered dynamically.
        this.committed = false;

        // We store whether the form values have been attempted to be submitted to the server.
        this.submitted = false;

        // We store whether the form values have been submitted and saved to the server.
        this.completed = false;

        // Form validation errors are stored on each validate.
        // Validation first happens on form submit and thereafter on every field change.
        this.errors = [];

        // Form field warnings are stored on each validate.
        this.warnings = [];

        this.configBody();
        this.setTimezoneValue();
        this.setTimezoneOffsetValue();

        // Set up our field components
        this.inquiryInterestsField = InquiryInterestsField.new(this);
        this.locationField = LocationField.new(this);
        this.quantityPeopleField = QuantityPeopleField.new(this);
        this.datesField = DatesField.new(this);
        this.messageField = MessageField.new(this);
        this.personNameField = PersonNameField.new(this);
        this.personEmailField = PersonEmailField.new(this);
        this.agreementField = AgreementField.new(this);
        // this.currentPersonField = CurrentPersonField.new(this);

        // Make some fieldsets into components.
        this.buttonsFieldset = ButtonsFieldset.new(this);

        // Make components from the form's submit buttons.
        this.submitButton = SubmitButton.new(this);

        // Create an element to house our communications (error and warning messages).
        this.commsElement = this.buttonsFieldset.element.parentNode.insertBefore(InquiryForm.createCommsElement(), this.buttonsFieldset.element);

        // Create an element to house our error messages.
        this.errorsElement = this.commsElement.appendChild(InquiryForm.createErrorsElement());

        // Create an element to house our warning messages.
        this.warningsElement = this.commsElement.appendChild(InquiryForm.createWarningsElement());

        // This event is fired by child field components whenever their input/select is changed.
        // All the field's values are checked for errors and warnings. We get the field warnings.
        // If the form has already been submitted before then we get the field errors.
        // Then we render everything (including the fields).
        this.element.addEventListener(InquiryFormConfig.changeEventName, () => {
            this.interacted = true;
            this.warnings = this.getFieldWarnings();
            if (this.isCommitted()) {
                this.errors = this.getFieldErrors()
            }
            this.submitted = false; // Changing a field will reset the `submitted` state.
            this.completed = false; // Changing a field will reset the `completed` state.
            this.render();
        });

        // When the form is submitted we mark it as interacted, committed.
        // We then update the fields to get the typecast values and any warnings or errors
        // We store warnings and errors from the fields.
        // And then we render everything (including the fields).
        // And if there are no errors then we submit.
        this.formElement.addEventListener('submit', (event) => {
            this.handleCommit();
            if (this.hasErrors()) {
                event.preventDefault()
            } else {
                this.submitted = true;
                this.loading['submitted'] = `submitting`;
                this.fireSubmitEvent();
                if (this.submitWithXHR && this.getModalbox()) {
                    this.submitToModalbox();
                    event.preventDefault();
                }
            }
            this.render();
        });

        this.inited = true;
        this.render();
    }

    configBody() {
        // Set the body's background to the same as this element's background. This hides the edges of the element when it is smaller than the viewport.
        // this.document.body.style.backgroundColor = this.window.getComputedStyle(this.element)['backgroundColor'];
    }

    // Set the timezone hidden input with the clients timezone
    setTimezoneValue() {
        let timezoneInput = this.element.querySelector('input[name="inquiry[timezone]"]');
        if (timezoneInput && (Intl && Intl.DateTimeFormat && Intl.DateTimeFormat().resolvedOptions)) {
            let tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tzid) {
                timezoneInput.value = tzid
            }
        }
    }

    // Set the timezone_offset hidden input with the clients timezone offset
    setTimezoneOffsetValue() {
        let timezoneOffsetInput = this.element.querySelector('input[name="inquiry[timezone_offset]"]');
        if (timezoneOffsetInput) {
            timezoneOffsetInput.value = new Date().getTimezoneOffset()
        }
    }

    handleCommit() {
        this.committed = true;
        this.interacted = true;
        this.updateFields();
        this.warnings = this.getFieldWarnings();
        this.errors = this.getFieldErrors();
    }

    updateFields() {
        if (this.inquiryInterestsField) {
            this.inquiryInterestsField.update()
        }
        if (this.locationField) {
            this.locationField.update()
        }
        if (this.quantityPeopleField) {
            this.quantityPeopleField.update()
        }
        if (this.datesField) {
            this.datesField.update()
        }
        if (this.messageField) {
            this.messageField.update()
        }
        if (this.personNameField) {
            this.personNameField.update()
        }
        if (this.personEmailField) {
            this.personEmailField.update()
        }
        if (this.agreementField) {
            this.agreementField.update()
        }
    }

    getFieldErrors() {
        let errors = {};
        if (this.inquiryInterestsField && this.inquiryInterestsField.hasErrors()) {
            errors['inquiryInterests'] = this.inquiryInterestsField.errors
        }
        if (this.locationField && this.locationField.hasErrors()) {
            errors['location'] = this.locationField.errors
        }
        if (this.quantityPeopleField && this.quantityPeopleField.hasErrors()) {
            errors['quantityPeople'] = this.quantityPeopleField.errors
        }
        if (this.datesField && this.datesField.hasErrors()) {
            errors['dates'] = this.datesField.errors
        }
        if (this.messageField && this.messageField.hasErrors()) {
            errors['message'] = this.messageField.errors
        }
        if (this.personNameField && this.personNameField.hasErrors()) {
            errors['personName'] = this.personNameField.errors
        }
        if (this.personEmailField && this.personEmailField.hasErrors()) {
            errors['personEmail'] = this.personEmailField.errors
        }
        if (this.agreementField && this.agreementField.hasErrors()) {
            errors['agreement'] = this.agreementField.errors
        }
        return errors;
    }

    getFieldWarnings() {
        let warnings = {};
        if (this.inquiryInterestsField && this.inquiryInterestsField.hasWarnings()) {
            warnings['inquiryInterests'] = this.inquiryInterestsField.warnings
        }
        if (this.locationField && this.locationField.hasWarnings()) {
            warnings['location'] = this.locationField.warnings
        }
        if (this.quantityPeopleField && this.quantityPeopleField.hasWarnings()) {
            warnings['quantityPeople'] = this.quantityPeopleField.warnings
        }
        if (this.datesField && this.datesField.hasWarnings()) {
            warnings['dates'] = this.datesField.warnings
        }
        if (this.messageField && this.messageField.hasWarnings()) {
            warnings['message'] = this.messageField.warnings
        }
        if (this.personNameField && this.personNameField.hasWarnings()) {
            warnings['personName'] = this.personNameField.warnings
        }
        if (this.personEmailField && this.personEmailField.hasWarnings()) {
            warnings['personEmail'] = this.personEmailField.warnings
        }
        if (this.agreementField && this.agreementField.hasWarnings()) {
            warnings['agreement'] = this.agreementField.warnings
        }
        return warnings;
    }

    hasErrors() {
        const keys = Object.keys(this.errors);
        return (keys.length > 0);
    }

    getErrorMessages() {
        let messages = [];
        Object.keys(this.errors).forEach((key) => {
            this.errors[key].forEach((message) => {
                messages.push(message);
            })
        })
        return messages;
    }

    renderErrors() {
        const messages = this.getErrorMessages();
        const baseClass = InquiryFormConfig.errorsBaseHtmlClass;
        this.errorsElement.innerHTML = `<ul class="${baseClass}__messages">${messages.map((message) => {
            return `<li class="${baseClass}__message">${message}</li>`
        }).join('')}</ul>`;
    }

    hasWarnings() {
        const keys = Object.keys(this.warnings);
        return (keys.length > 0);
    }

    getWarningMessages() {
        let messages = [];
        Object.keys(this.warnings).forEach((key) => {
            this.warnings[key].forEach((message) => {
                messages.push(message);
            })
        })
        return messages;
    }

    renderWarnings() {
        const messages = this.getWarningMessages();
        const baseClass = InquiryFormConfig.warningsBaseHtmlClass;
        this.warningsElement.innerHTML = `<ul class="${baseClass}__messages">${messages.map((message) => {
            return `<li class="${baseClass}__message">${message}</li>`
        }).join('')}</ul>`;
    }

    hasInteraction() {
        return (this.interacted);
    }

    isCommitted() {
        return (this.committed);
    }

    isSubmitted() {
        return (this.submitted);
    }

    isCompleted() {
        return (this.completed);
    }

    isLoading() {
        return !isEmpty(this.loading);
    }

    isInited() {
        return (this.inited);
    }

    render() {
        this.conditionallySetHtmlClassState(this.isLoading(), 'is_loading');
        this.conditionallySetHtmlClassState(this.isInited(), 'inited');
        this.conditionallySetHtmlClassState(this.hasErrors(), 'has_errors');
        this.conditionallySetHtmlClassState(this.hasWarnings(), 'has_warnings');
        this.conditionallySetHtmlClassState(this.hasInteraction(), 'has_interaction');
        this.conditionallySetHtmlClassState(this.isCommitted(), 'is_committed');
        this.conditionallySetHtmlClassState(this.isSubmitted(), 'is_submitted');
        this.conditionallySetHtmlClassState(this.isCompleted(), 'is_completed');

        // Render field components…
        if (this.inquiryInterestsField) {
            this.inquiryInterestsField.render()
        }
        if (this.locationField) {
            this.locationField.render()
        }
        if (this.quantityPeopleField) {
            this.quantityPeopleField.render()
        }
        if (this.datesField) {
            this.datesField.render()
        }
        if (this.messageField) {
            this.messageField.render()
        }
        if (this.personNameField) {
            this.personNameField.render()
        }
        if (this.personEmailField) {
            this.personEmailField.render()
        }
        if (this.agreementField) {
            this.agreementField.render()
        }
        // if (this.currentPersonField) { this.currentPersonField.render() }

        // Render fieldset components…
        if (this.buttonsFieldset) {
            this.buttonsFieldset.render()
        }

        // Render button components…
        if (this.submitButton) {
            this.submitButton.render()
        }

        this.renderErrors();
        this.renderWarnings();

        // if (this.statusBar) { this.statusBar.render( this.loading ) };

        fireDOMUpdateEvent();
    }

    fireChangeEvent() {
        const eventName = InquiryFormConfig.changeEventName;
        this.element.dispatchEvent(new Event(eventName));
    }

    fireSubmitEvent() {
        const eventName = InquiryFormConfig.submitEventName;
        this.element.dispatchEvent(new Event(eventName));
    }

    // Generate a URL from the form action and field values that can be used by a XHR request.
    getFormSubmitUrl() {
        return this.formElement.getAttribute('action');
    }

    // Get the forms method.
    getFormSubmitMethod() {
        return this.formElement.getAttribute('method');
    }

    // Get the serialized forms values (parameters to send via POST).
    getFormSubmitParams() {
        return FormSerialize(this.formElement);
    }

    // Extract the content as a HTML string from a template element.
    getContentForSubmitting() {
        const templateElement = this.element.querySelector('.inquiry_form__content_for_submitting');
        if (templateElement) return templateElement.innerHTML;
        return "<p>Submitting your request.</p>";
    }

    submitToModalbox() {

        this.showModalboxContent(this.contentForSubmitting);

        const handleResponse = function (response) {
            delete this.loading['submitted'];
            this.showModalboxContent(response);
            new DOMUpdateEvent().fire();
            this.render();
        };

        const loadSuccess = function (response) {
            this.completed = true;
            handleResponse.bind(this, response)();
        };

        const loadFail = function (response) {
            handleResponse.bind(this, response)();
        };

        // Validation failures come back as HTTP 200 (the create action re-renders the
        // form partial via format.js), so success and validation-failure both resolve
        // through .then; .catch only fires on a genuine network/5xx error.
        new XHRFetcher(this.getFormSubmitUrl(), {
            params: this.getFormSubmitParams(),
            method: this.getFormSubmitMethod(),
            headers: {'Accept': 'text/javascript'}
        }).fetch()
            .then(loadSuccess.bind(this))
            .catch(loadFail.bind(this));
    }

    getModalbox() {
        return getGlobalComponentOfType(ModalboxConfig.componentTypeKey);
    }

    showModalboxContent(content) {
        showModalboxContent(this.getModalbox(), content)
    }

}

function fireDOMUpdateEvent() {
    if (DOMUpdateEvent) {
        new DOMUpdateEvent({fireOnDOMContentLoaded: false}).fire();
    } else {
        console.warn("[InquiryForm] DOMUpdateEvent was not fired.");
    }
}

function showModalboxContent(modalbox, content) {
    if (!content) return;
    if (!modalbox) {
        console.warn("[InquiryForm] Could not find Modalbox instance!")
        return;
    }
    modalbox.open();
    modalbox.update(content);
}
